import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ActivityCard from "../Customer/Activities/ActivityCard/ActivityCard";

const Activities = ({ activitiesData, error, isLoading, refetch }) => {
  const [timers, setTimers] = useState({});
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  const activities = useMemo(() => {
    if (!activitiesData) return [];
    return activitiesData.data || activitiesData || [];
  }, [activitiesData]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize(); // Initial run
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (activities.length === 0 && !isLoading) {
      toast.info("No activities available.");
    }
  }, [activities, isLoading]);

  useEffect(() => {
    const interval = setInterval(() => {
      const updatedTimers = {};
      activities.forEach((activity) => {
        if (!activity.infinite_time && activity.end_date) {
          const now = new Date();
          const endTime = new Date(activity.end_date);
          const diff = endTime.getTime() - now.getTime();

          if (diff > 0) {
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
            const minutes = Math.floor((diff / (1000 * 60)) % 60);
            const seconds = Math.floor((diff / 1000) % 60);
            updatedTimers[activity.activity_id] = `${days}d ${hours
              .toString()
              .padStart(2, "0")}h ${minutes
              .toString()
              .padStart(2, "0")}m ${seconds.toString().padStart(2, "0")}s`;
          } else {
            updatedTimers[activity.activity_id] = "Expired";
          }
        }
      });
      setTimers(updatedTimers);
    }, 1000);
    return () => clearInterval(interval);
  }, [activities]);

  const handleViewDetails = (activityId) => {
    navigate(`/ActivitiesDetails/${activityId}`);
  };

  const displayedActivities = useMemo(() => {
    if (isMobile) {
      return activities.slice(0, 6);
    } else {
      return showAllActivities ? activities : activities.slice(0, 5);
    }
  }, [activities, showAllActivities, isMobile]);

  const shouldShowToggleButton = activities.length > 6 && !isMobile;

  const handleToggleView = () => {
    setShowAllActivities((prev) => !prev);
  };

  useEffect(() => {
    if (error) {
      toast.error("Failed to load activities.");
    }
  }, [error]);

  if (isLoading) {
    return (
      <div className="bg-white flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FE7A3A]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white flex flex-col items-center justify-center text-gray-700 text-xl p-4 h-screen">
        <p className="text-red-500">
          Error: Failed to load activities. Please try again later.
        </p>
        <button
          onClick={refetch}
          className="mt-4 px-4 py-2 bg-[#FE7A3A] text-white rounded-lg hover:bg-orange-500"
        >
          Retry Loading Activities
        </button>
      </div>
    );
  }

  // No activities
  if (activities.length === 0) {
    return (
      <div className="bg-white flex items-center justify-center text-gray-600 text-xl p-4 h-screen">
        <p>No activities available yet. Check back soon!</p>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="pt-0 pb-4 px-4 sm:px-6 md:px-8 max-w-screen-2xl mx-auto">
        <div className="flex justify-between items-center mb-6 mt-2">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">
              Local Activities & Events
            </h2>
            <p className="text-sm md:text-base text-gray-700">
              Connect with people who share your interests
            </p>
          </div>

          {shouldShowToggleButton && (
            <button
              onClick={handleToggleView}
              className="text-base font-semibold text-orange-500 hover:text-orange-600 transition-colors duration-200"
            >
              {showAllActivities ? "View Less" : "View All"}
            </button>
          )}
        </div>
        <div
          className="
            grid gap-x-4 gap-y-3
            grid-cols-2
            sm:grid-cols-2
            md:grid-cols-3
            lg:grid-cols-4
            xl:grid-cols-5
            2xl:grid-cols-5
          "
        >
          {displayedActivities.map((activity) => (
            <ActivityCard
              key={activity.activity_id}
              activity={activity}
              timerValue={timers[activity.activity_id]}
              handleViewDetails={handleViewDetails}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Activities;
